import { createContext, useContext, useEffect, useRef } from "react";
import { useQuery } from "@web-monorepo/fetchers";
import { useQueryClient } from "@tanstack/react-query";
import { fetchParentReferralAccount, parentReferralAccountQueryKey } from "./parentReferralAccountQuery";
import { getSessionData } from "#/src/hooks/useSession";
import useRedeemParentReferralMutation, {
  type ParentReferralRedeemResponse,
} from "#/src/hooks/api/parent/useRedeemParentReferralMutation";
import useSessionDiscountsQuery, { SESSION_DISCOUNTS_QUERY_KEY } from "#/src/hooks/useSessionDiscountsQuery";
import type { ParentReferralAccount } from "./parentReferralOffer";
import { useRefreshParentReferralOffer } from "./useRefreshParentReferralOffer";

interface ClaimScope {
  userId: string | undefined;
  active: boolean;
  attempted: boolean;
  completedResponse?: ParentReferralRedeemResponse;
  inFlight?: Promise<ParentReferralRedeemResponse | undefined>;
}

export function useClaimOwner(userId: string | undefined) {
  const queryClient = useQueryClient();
  const scopeRef = useRef<ClaimScope>({ userId, active: true, attempted: false });
  if (scopeRef.current.userId !== userId) {
    scopeRef.current = { userId, active: true, attempted: false };
  }
  // Each request captures its own user scope; settling an old request cannot mutate the new one.
  const scope = scopeRef.current;
  const queryKey = parentReferralAccountQueryKey(userId);
  const account = useQuery(queryKey, () => fetchParentReferralAccount(queryClient, userId), {
    enabled: Boolean(userId),
    refetchOnWindowFocus: "always",
    onError: () => undefined,
  });
  const mutation = useRedeemParentReferralMutation();
  const claim = account.data?.recipientClaim;
  const balance = useSessionDiscountsQuery({
    enabled: Boolean(userId && (claim?.recipientGrantedAt || scope.completedResponse?.result === "granted")),
    userId,
  });

  useRefreshParentReferralOffer(
    account.data?.currentOffer,
    account.dataUpdatedAt,
    account.refetch,
    Boolean(userId),
    userId,
  );

  useEffect(() => {
    scope.active = true;
    return () => {
      scope.active = false;
      if (userId) {
        void queryClient.cancelQueries({ queryKey: parentReferralAccountQueryKey(userId) });
        queryClient.removeQueries({ queryKey: parentReferralAccountQueryKey(userId) });
        void queryClient.cancelQueries({ queryKey: [...SESSION_DISCOUNTS_QUERY_KEY, userId] });
        queryClient.removeQueries({ queryKey: [...SESSION_DISCOUNTS_QUERY_KEY, userId] });
      }
    };
  }, [queryClient, scope, userId]);

  function isActive() {
    return (
      scopeRef.current === scope && scope.active && userId != null && getSessionData()?.user_details.uid === userId
    );
  }

  function complete(referralCode?: string): Promise<ParentReferralRedeemResponse | undefined> {
    if (scope.inFlight) return scope.inFlight;
    if (!isActive()) return Promise.resolve(undefined);
    if (scope.completedResponse) return Promise.resolve(scope.completedResponse);
    const saved = queryClient.getQueryData<ParentReferralAccount>(queryKey);
    if (!saved) return Promise.resolve(undefined);
    if (saved.recipientClaim?.recipientGrantedAt) {
      return Promise.resolve({
        result: "already_granted",
        recipientClaim: saved.recipientClaim,
        grantedAt: saved.recipientClaim.recipientGrantedAt,
        expiresAt: saved.recipientClaim.recipientCreditsExpireAt,
        isRedeemed: false,
      });
    }
    if (!saved.recipientClaim && !referralCode) return Promise.resolve(undefined);
    scope.attempted = true;
    const request = mutation
      .mutateAsync({ body: saved.recipientClaim ? {} : { referralCode } })
      .then((response) => {
        if (!isActive()) return undefined;
        if (response.body.result === "granted") scope.completedResponse = response.body;
        if (response.body.result !== "ineligible" && response.body.recipientClaim) {
          const recipientClaim = response.body.recipientClaim;
          queryClient.setQueryData<ParentReferralAccount>(queryKey, (previous) =>
            previous ? { ...previous, recipientClaim } : previous,
          );
        }
        void queryClient.invalidateQueries({ queryKey });
        void queryClient.invalidateQueries({ queryKey: SESSION_DISCOUNTS_QUERY_KEY });
        return response.body;
      })
      .finally(() => {
        scope.inFlight = undefined;
      });
    scope.inFlight = request;
    return request;
  }

  return {
    userId,
    accountQuery: account,
    claim,
    legacyGrant:
      scope.completedResponse?.result === "granted" && !scope.completedResponse.recipientClaim
        ? scope.completedResponse
        : undefined,
    isResolved: account.data !== undefined,
    isLookupError: account.isError,
    isCompleting: scope.attempted && mutation.isLoading,
    isCompletionError: scope.attempted && mutation.isError,
    lookupUpdatedAt: account.dataUpdatedAt,
    complete,
    retryLookup: account.refetch,
    retryBalance: balance.refetch,
    balance: balance.isError
      ? { status: "error" as const }
      : balance.data === undefined
        ? { status: "loading" as const }
        : {
            status: "ready" as const,
            // The endpoint returns only unused credits; exclude expired rows and other promotions.
            availableCount: balance.data.filter(
              (credit) =>
                credit.promotionType === "parent_referral_recipient" &&
                (credit.expiresAt == null || new Date(credit.expiresAt).getTime() > Date.now()),
            ).length,
          },
  };
}

export const ParentReferralClaimContext = createContext<ReturnType<typeof useClaimOwner> | null>(null);

export function useParentReferralClaim() {
  const owner = useContext(ParentReferralClaimContext);
  if (!owner) throw new Error("ParentReferralClaimProvider is required.");
  return owner;
}

export function useResumeParentReferralClaim() {
  const owner = useParentReferralClaim();
  const attemptedLookup = useRef<string>();
  useEffect(() => {
    if (
      !owner.claim ||
      owner.claim.recipientGrantedAt ||
      attemptedLookup.current === `${owner.userId}:${owner.lookupUpdatedAt}`
    ) {
      return;
    }
    attemptedLookup.current = `${owner.userId}:${owner.lookupUpdatedAt}`;
    // Background recovery owns data only; the active referral interaction owns feedback/navigation.
    void owner.complete().catch(() => undefined);
  }, [owner]);
}
